/**
 * NotificationPrefsPage.tsx — 2004va
 * User sets how they want to be notified.
 * Accessible from the More drawer.
 * Logic unchanged from 1904va — styling aligned to Room design tokens.
 */
import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Send, MessageCircle, Phone, Mail, Bell, BellOff, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'

type Channel   = 'telegram' | 'whatsapp' | 'sms' | 'email' | 'push' | 'none'
type Frequency = 'immediate' | 'digest_2h' | 'daily'

const CHANNELS: {
  value:     Channel
  label:     string
  icon:      React.ReactNode
  desc:      string
  available: boolean
}[] = [
  { value: 'telegram',  label: 'Telegram',  icon: <Send size={15} />,          desc: 'Best experience — inline button, instant',          available: true  },
  { value: 'whatsapp',  label: 'WhatsApp',  icon: <MessageCircle size={15} />, desc: 'Coming soon — pending Meta verification',            available: false },
  { value: 'sms',       label: 'SMS',       icon: <Phone size={15} />,         desc: 'Universal fallback — plain link',                    available: true  },
  { value: 'email',     label: 'Email',     icon: <Mail size={15} />,          desc: 'Reliable but may go to spam',                        available: true  },
  { value: 'push',      label: 'App push',  icon: <Bell size={15} />,          desc: 'Best if app installed — opens directly',             available: true  },
  { value: 'none',      label: 'None',      icon: <BellOff size={15} />,       desc: 'In-app inbox only',                                  available: true  },
]

const FREQUENCIES: { value: Frequency; label: string; desc: string }[] = [
  { value: 'immediate', label: 'Immediately',   desc: 'Every match, as it happens' },
  { value: 'digest_2h', label: 'Every 2 hours', desc: 'Batched digest'             },
  { value: 'daily',     label: 'Daily',         desc: 'Once a day summary'         },
]

export default function NotificationPrefsPage() {
  const { participant } = useAuthStore()
  const qc = useQueryClient()

  const { data: prefs, isLoading } = useQuery({
    queryKey: ['notif-prefs', participant?.id],
    queryFn: async () => {
      if (!participant?.id) return null
      const { data } = await supabase
        .from('notification_prefs')
        .select('*')
        .eq('participant_id', participant.id)
        .single()
      return data
    },
    enabled: !!participant?.id,
  })

  const [channel,    setChannel]    = useState<Channel>('email')
  const [frequency,  setFrequency]  = useState<Frequency>('immediate')
  const [telegramId, setTelegramId] = useState('')
  const [phone,      setPhone]      = useState('')
  const [email,      setEmail]      = useState('')
  const [saved,      setSaved]      = useState(false)

  useEffect(() => {
    if (prefs) {
      setChannel(prefs.channel     ?? 'email')
      setFrequency(prefs.frequency ?? 'immediate')
      setTelegramId(prefs.telegram_chat_id ?? '')
      setPhone(prefs.phone_number  ?? '')
      setEmail(prefs.email         ?? '')
    }
  }, [prefs])

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!participant?.id) throw new Error('Not authenticated')
      const { error } = await supabase
        .from('notification_prefs')
        .upsert({
          participant_id:   participant.id,
          channel,
          frequency,
          telegram_chat_id: telegramId.trim() || null,
          phone_number:     phone.trim()      || null,
          email:            email.trim()      || null,
          updated_at:       new Date().toISOString(),
        }, { onConflict: 'participant_id' })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      qc.invalidateQueries({ queryKey: ['notif-prefs'] })
    },
  })

  if (!participant) return (
    <div className="flex items-center justify-center h-64 text-room-muted text-sm">
      Sign in to manage notifications
    </div>
  )

  return (
    <div className="space-y-6 animate-fade-in">

      <div>
        <h2 className="text-white font-bold text-lg">Notifications</h2>
        <p className="text-room-muted text-xs mt-0.5">
          How should The Room reach you when matched content arrives?
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner size={24} /></div>
      ) : (
        <>
          {/* Channel picker */}
          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-room-muted uppercase tracking-widest mb-2">Channel</p>
            {CHANNELS.map(ch => (
              <button
                key={ch.value}
                type="button"
                disabled={!ch.available}
                onClick={() => ch.available && setChannel(ch.value)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all',
                  !ch.available && 'opacity-40 cursor-not-allowed',
                  channel === ch.value && ch.available
                    ? 'border-room-accent bg-room-accent/10'
                    : 'border-room-border bg-room-surface hover:border-room-muted'
                )}
              >
                <div className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  channel === ch.value ? 'bg-room-accent/20 text-room-accent' : 'bg-room-border text-room-muted'
                )}>
                  {ch.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-semibold', channel === ch.value ? 'text-white' : 'text-room-text')}>
                    {ch.label}
                  </p>
                  <p className="text-xs text-room-muted">{ch.desc}</p>
                </div>
                {channel === ch.value && ch.available && (
                  <Check size={14} className="text-room-accent flex-shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Channel-specific detail inputs */}
          {channel === 'telegram' && (
            <div className="bg-room-surface border border-room-border rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-white">Your Telegram Chat ID</p>
              <p className="text-xs text-room-muted">
                Message <span className="text-white">@userinfobot</span> on Telegram — it will reply with your chat ID. Paste it below.
              </p>
              <input
                value={telegramId}
                onChange={e => setTelegramId(e.target.value)}
                placeholder="e.g. 123456789"
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
              />
            </div>
          )}

          {channel === 'sms' && (
            <div className="bg-room-surface border border-room-border rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-white">Mobile number</p>
              <input
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+44 7700 900000"
                type="tel"
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
              />
            </div>
          )}

          {channel === 'email' && (
            <div className="bg-room-surface border border-room-border rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-white">Email address</p>
              <input
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
                className="w-full bg-black/30 border border-room-border rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-room-muted focus:outline-none focus:border-room-accent/50 transition-colors"
              />
            </div>
          )}

          {/* Frequency */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-room-muted uppercase tracking-widest">How often</p>
            <div className="grid grid-cols-3 gap-2">
              {FREQUENCIES.map(f => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFrequency(f.value)}
                  className={cn(
                    'px-3 py-3 rounded-xl border text-left transition-all',
                    frequency === f.value
                      ? 'border-room-accent bg-room-accent/10 text-white'
                      : 'border-room-border bg-room-surface text-room-muted hover:border-room-muted'
                  )}
                >
                  <p className="font-semibold text-xs">{f.label}</p>
                  <p className="text-[10px] opacity-60 mt-0.5 leading-tight">{f.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Save */}
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className={cn(
              'w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2',
              saved
                ? 'bg-emerald-600 text-white'
                : 'bg-room-accent hover:bg-room-accent/90 text-white shadow-lg shadow-room-accent/20',
              saveMutation.isPending && 'opacity-70 cursor-not-allowed'
            )}
          >
            {saveMutation.isPending
              ? <Spinner size={15} />
              : saved
              ? <><Check size={15} /> Saved</>
              : 'Save preferences'
            }
          </button>

          {saveMutation.isError && (
            <p className="text-room-error text-xs text-center">
              {(saveMutation.error as Error).message}
            </p>
          )}
        </>
      )}
    </div>
  )
}
